import { Box, Button, Stack, Tooltip, Typography } from '@mui/material'
import { observer } from 'mobx-react'
import React from 'react'
import { FormattedMessage } from 'react-intl'
import EquipmentCheckingField from '../../../../../components/FormElements/EquipmentCheckingField'
import FieldError from '../../../../../components/FormElements/FieldError'
import FilterDatePicker from '../../../../../components/FormElements/FilterDatePicker/FilterDatePicker'
import FormReceiverAutocomplete from '../../../../../components/FormElements/FormReceiverAutocomplete'
import DataUnit from '../../../../../components/customElements/DataUnit/DataUnit'
import FileInput from '../../../../../components/customElements/FileInput/FileInput'
import InputField from '../../../../../components/customElements/InputField/InputField'
import UploadedFileItem from '../../../../../components/customElements/UploadedFileItem'
import { getImageSrc } from '../../../../../helpers/getImageSrc'
import { notifyError } from '../../../../../services/ToastifyService/ToastifyService'
import { formatSN } from '../../../../../utils/utils/formatSN'
import { RequestType } from '../../../types/Request.type'
import FormSelect from '../elements/FormSelect'
import InputLabel from '../elements/InputLabel'
import TypedAccordion from '../elements/TypedAccordion'
import WarnNotification from '../elements/WarnNotification'
import { useDrawerContentInstallation } from '../hooks/useDrawerContentInstallation'

type DrawerContentInstallationProps = {
  request: RequestType
  onClose: () => void
}

export const DrawerContentInstallation: React.FC<
  DrawerContentInstallationProps
> = ({ request, onClose }) => {
  const {
    handleSubmit,
    onSubmit,
    control,
    receiveresModels,
    state,
    defaultValues,
    files,
    deleteFile,
    setComment,
    comment,
    intl,
    isValid,
    isAtLeastOneChanged,
    onSave,
    goBack,
    disableUploadField,
    photoPermission,
    handleSetFiles,
    showNotification,
    notifications,
    setShowNotification,
    setValue,
    onCheckEquipment,
    watch,
    errors,
    lastCheckedId,
    setError,
  } = useDrawerContentInstallation(request, onClose)

  return (
    <Stack
      height={'100%'}
      justifyContent={'space-between'}
      component={'form'}
      onSubmit={handleSubmit(onSubmit)}
    >
      <TypedAccordion summary={'Данные установки'}>
        <Stack spacing={'10px'}>
          <InputLabel label='ID оборудования'>
            <EquipmentCheckingField
              control={control}
              defaultEquipmentId={request?.equipmentId ?? ''}
              equipmentId={watch('equipmentId')!}
              lastCheckedId={lastCheckedId ?? ''}
              onChecked={onCheckEquipment}
              error={!!errors.equipmentId}
              hideCheckBtn={!showManufacturer(request)}
            />
          </InputLabel>
          {showManufacturer(request) && (
            <InputLabel label='S/N оборудования'>
              <>
                {watch('hasCas') && (
                  <DataUnit
                    name=''
                    value={formatSN(watch('receiverSerialNumber'))}
                  />
                )}
                {!watch('hasCas') && (
                  <FormReceiverAutocomplete
                    control={control}
                    name='receiverSerialNumber'
                    fullWidth
                    label=''
                    setValue={setValue as any}
                    setError={setError as any}
                    modelName='receiverModelId'
                    placeholder='Введите серийный номер'
                  />
                )}
              </>
            </InputLabel>
          )}
          {watch('smartCardId') && (
            <FieldError msg='В указанный преимник встроена смарт-карта с другим Id!' />
          )}
          {showManufacturer(request) && (
            <InputLabel label='Модель оборудования'>
              <>
                {watch('hasCas') && (
                  <DataUnit name='' value={watch('receiverModelId')} />
                )}
                {!watch('hasCas') && (
                  <FormSelect
                    control={control}
                    fullWidth
                    name='receiverModelId'
                    label=''
                    items={
                      receiveresModels.map(
                        (el: any) => `${el.manufacturerName} - ${el.name}`,
                      ) ?? []
                    }
                    disabled
                    placeholder='Выберите модель'
                  />
                )}
              </>
            </InputLabel>
          )}
          {state.canChangeInstallationData && (
            <Stack
              direction={'row'}
              spacing={'10px'}
              width={'100%'}
              alignItems={'center'}
            >
              {state.reportPeriodError && (
                <Tooltip
                  title={
                    'Дату установки можно отредактировать только в рамках отчётного периода текущего отчёта'
                  }
                >
                  <img
                    src={getImageSrc('tooltip')}
                    alt={''}
                    style={tooltipSx}
                  />
                </Tooltip>
              )}
              <InputLabel label='Дата установки'>
                <FilterDatePicker
                  control={control}
                  name={'installationDate'}
                  label=''
                  customError={state.reportPeriodError}
                  defaultValue={defaultValues.installationDate}
                />
              </InputLabel>
            </Stack>
          )}

          {state.canUploadImages && (
            <Stack py={'16px'} spacing={'16px'}>
              {files?.length && (
                <>
                  {files.map((img) => (
                    <UploadedFileItem
                      img={img}
                      key={img.url}
                      deleteFile={deleteFile}
                    />
                  ))}
                </>
              )}
              {!disableUploadField && (
                <Box sx={{ paddingTop: files?.length ? '8px' : 0 }}>
                  <Typography marginBottom={'10px'}>
                    {intl.formatMessage(
                      {
                        id: `Загрузите от {minimumLimit} до {maximumLimit} фотографий`,
                        defaultMessage: `Загрузите от {minimumLimit} до {maximumLimit} фотографий`,
                      },
                      {
                        minimumLimit: photoPermission.minLimitPerLoad,
                        maximumLimit: photoPermission.maxLimitPerLoad,
                      },
                    )}
                  </Typography>
                  <FileInput
                    multiple={true}
                    file={null}
                    setFile={handleSetFiles}
                    fileTypes={['JPEG', 'JPG', 'PNG']}
                    fileName
                    label={intl.formatMessage({
                      id: 'Загрузите или перетащите файл сюда',
                    })}
                    uploadedLabel={intl.formatMessage({
                      id: 'Загрузите или перетащите файл сюда',
                    })}
                    classes={{}}
                  />
                </Box>
              )}
              {showNotification && (
                <WarnNotification
                  notifications={notifications}
                  setIsShown={setShowNotification}
                  title='Изменение ID оборудования или Даты установки'
                />
              )}
            </Stack>
          )}
          {state.shouldShowCommentField && (
            <Stack sx={{ paddingY: '16px' }}>
              <InputField
                multiline
                value={comment}
                onChange={(e: React.ChangeEvent<any>) =>
                  setComment(e.target.value)
                }
                rows={4}
                fullWidth
                territorySelect={false}
                placeholder={intl.formatMessage({ id: 'Введите комментарий' })}
                error={!comment}
              />
            </Stack>
          )}
        </Stack>
      </TypedAccordion>
      <Box px={2}>
        {showManufacturer(request) && (
          <Button
            color={'primary'}
            variant={'contained'}
            sx={{ margin: '24px 0 0 0' }}
            fullWidth
            type='submit'
            disabled={!getIsValid(isValid, request) || !isAtLeastOneChanged}
          >
            <FormattedMessage id={'сохранить'} />
          </Button>
        )}
        {!showManufacturer(request) && (
          <Button
            color={'primary'}
            variant={'contained'}
            sx={{ margin: '24px 0 0 0' }}
            fullWidth
            type='button'
            onClick={onSave}
            disabled={state.disableSaveBtn || !getIsValid(isValid, request)}
          >
            <FormattedMessage id={'сохранить'} />
          </Button>
        )}
        <Button
          color={'primary'}
          variant={'outlined'}
          onClick={() => goBack()}
          fullWidth
          sx={{ margin: '8px 0 24px 0' }}
        >
          <FormattedMessage id={'Отмена'} />
        </Button>
      </Box>
    </Stack>
  )
}

const tooltipSx = {
  width: '18px',
  marginTop: 0,
  marginBottom: '-4px',
}

export type CustomFileType = { file: File; url: string }

const showManufacturer = (request: RequestType) =>
  ![5, 9, 15, 16].includes(request.status)

const getIsValid = (isValid: boolean, request: RequestType) => {
  if ([5, 9, 15, 16].includes(request.status)) {
    return true
  }
  return isValid
}

export const checkUploadedImages = (res: any) => {
  const { uploaded, message, images } = res
  const errorImages = images.filter((el: any) => !el.isValid)

  if (errorImages.length) {
    errorImages.forEach((el: any) => {
      notifyError(el.error + ': ' + el.filename)
    })

    return false
  }
  if (!uploaded) {
    if (!errorImages.length) {
      notifyError(message)
    }

    return false
  }

  return true
}

export default observer(DrawerContentInstallation)
